/**
 * Per-wallet custom-token import identity and merge.
 *
 * The Manage-crypto screen lets a user import a token the curated catalog does
 * not carry, by pasting its contract address and resolving its metadata against
 * the gateway. This framework-free module owns the pure pieces that behavior
 * rests on: the normalized shape of one imported token, the stable identity key
 * that dedupes an import within a wallet, and the merge that turns imported
 * entries into holding rows so an imported token flows through the same list,
 * filter, and dust rules as a curated one. Keeping these apart from React and
 * the store makes them directly unit-testable and lets the store, the import
 * sheet, and the Dashboard share one definition so the key can never drift
 * between where a token is imported and where it is matched.
 *
 * The identity is chain-and-contract aware on purpose. A contract address is
 * unique on one chain but the same address string can name unrelated tokens on
 * two chains, so the key combines the chain with the lowercased contract. The
 * symbol is deliberately left out of the key: two imports of the same contract
 * are the same token even if the user resolved slightly different metadata, so
 * the contract, not the symbol, is the identity.
 *
 * An imported token is always unverified. It never carries a curated allow-list
 * flag and its fiat value is always null, so it can badge as unverified and can
 * never inflate a portfolio total. The gateway enforces the same, so the client
 * treatment matches the server rather than trusting a client-supplied flag.
 */

import type { HoldingRow } from './holdings';
import { chainLabel } from './holdings';
import type { BalanceLine, WalletBalances } from './types';
// A type-only import of the chip shape the import sheet's network picker renders.
// It is erased at compile time, so the pure module gains no runtime dependency on
// the component that defines the chip.
import type { ChainChip } from '../components/ChainFilter';

/**
 * The largest number of imported tokens one wallet may carry. The gateway caps
 * the imports it will resolve and append at the same bound, so the client refuses
 * an add past this before the network rather than after a wasted round trip. The
 * two caps are kept equal so the client never lets through what the server would
 * drop.
 */
export const MAX_IMPORTED_TOKENS_PER_WALLET = 50;

/**
 * One imported custom token, as stored per wallet and later synced to the server.
 * The contract and chain are the identity; the symbol, name, and decimals are the
 * resolved metadata shown in the list. The network is an optional concrete key for
 * a multi-network wallet, so an EVM wallet can pin an import to Base rather than a
 * generic EVM tag; it is absent on a flat single-chain wallet whose one chain the
 * import already names.
 */
export interface ImportedToken {
  /**
   * The chain family key the token was imported under, for example "evm",
   * "tron", "solana", or "sui". This picks the gateway metadata reader and, with
   * the contract, forms the identity.
   */
  chain: string;
  /**
   * The concrete network key for a multi-network wallet, for example "base" or
   * "bsc", or undefined on a flat wallet. Display-only; it does not enter the
   * identity key, since one contract on one EVM address is one token regardless of
   * how it was labeled.
   */
  network?: string;
  /** The token contract address, as resolved. Stored verbatim for display. */
  contract: string;
  /** The resolved token symbol, for example "USDT". */
  symbol: string;
  /** The resolved full token name, for example "Tether USD". */
  name: string;
  /** The resolved smallest-unit decimals. */
  decimals: number;
}

/**
 * The stable identity for one imported token on one wallet. The chain is
 * lowercased and the contract is lowercased so a re-import of the same token
 * under different casing (a checksummed versus a lowercase EVM address) resolves
 * to one key and dedupes rather than doubling. The two are joined with a
 * separator that cannot occur in a chain key or a hex or base58 address.
 *
 * @param chain The chain family key the token was imported under.
 * @param contract The token contract address, case-insensitive.
 * @returns A key of the form "<chain>::<contract>".
 */
export function importKey(chain: string, contract: string): string {
  return `${chain.toLowerCase()}::${contract.toLowerCase()}`;
}

/**
 * The identity key for an imported token record, a thin wrapper over importKey so
 * callers that hold a record do not reach into its fields and risk keying on the
 * wrong pair.
 *
 * @param token The imported token.
 * @returns The token's stable per-wallet import key.
 */
export function tokenImportKey(token: Pick<ImportedToken, 'chain' | 'contract'>): string {
  return importKey(token.chain, token.contract);
}

/**
 * Deduplicate a list of imported tokens by their identity key, keeping the last
 * occurrence of each so a re-import overwrites the earlier metadata with the
 * freshly resolved one. Order follows first appearance, so an existing import
 * keeps its place in the list when it is re-resolved. Used by the store before it
 * persists and by the merge below so a duplicated entry never yields two rows.
 *
 * @param tokens The imported tokens, possibly carrying duplicates.
 * @returns The tokens with duplicates removed, last write winning per key.
 */
export function dedupeImported(tokens: readonly ImportedToken[]): ImportedToken[] {
  // Track the first-seen position per key so the output preserves insertion
  // order while a later duplicate replaces the metadata at that position.
  const order: string[] = [];
  const byKey = new Map<string, ImportedToken>();
  for (const token of tokens) {
    const key = tokenImportKey(token);
    if (!byKey.has(key)) order.push(key);
    byKey.set(key, token);
  }
  return order.map((key) => byKey.get(key) as ImportedToken);
}

/**
 * Whether a list of imported tokens already carries the given one, by identity.
 * Used to answer whether an add would be a re-import (allowed, it refreshes the
 * metadata) rather than a new entry counting against the cap.
 *
 * @param tokens The wallet's imported tokens.
 * @param token The candidate import.
 * @returns True when a token with the same identity key is already present.
 */
export function hasImported(
  tokens: readonly ImportedToken[],
  token: Pick<ImportedToken, 'chain' | 'contract'>,
): boolean {
  const key = tokenImportKey(token);
  return tokens.some((t) => tokenImportKey(t) === key);
}

/**
 * The concrete network chips for a wallet, derived from the badge chains its
 * holding rows already span. A multi-network EVM wallet that holds assets on Base
 * and BNB yields a chip per network, so the import sheet can pin an import to the
 * right one; a wallet on a single network yields at most one chip, which is not
 * worth a picker. The chips carry each chain's key so the sheet can render its real
 * logo through the shared resolver, matching the Manage-crypto sheet's own per-chain
 * chips. Kept here, apart from React, so it is directly unit-testable.
 *
 * @param rows The wallet's holding rows.
 * @returns One chip per distinct badge chain, in first-seen order.
 */
export function networkChipsFor(rows: readonly Pick<HoldingRow, 'badge' | 'network'>[]): ChainChip[] {
  const seen = new Map<string, ChainChip>();
  for (const row of rows) {
    if (seen.has(row.badge)) continue;
    const label = chainLabel(row.badge);
    seen.set(row.badge, {
      chainKey: row.badge,
      label,
      shortLabel: row.network || label,
    });
  }
  return [...seen.values()];
}

/**
 * Turn one imported token and its resolved balance line into a holding row. The
 * balance line comes from the gateway's balances response, where an imported
 * contract is appended as an extra entry with verified false and a null fiat, so
 * the row carries the live balance that refreshes on every poll while the symbol,
 * name, and decimals come from the stored import. The row is stamped unverified
 * and its fiat is forced null so an imported token badges as unverified and can
 * never contribute to a portfolio total, matching the gateway's own treatment.
 *
 * @param token The stored imported token.
 * @param line The matching appended balance line, or null when the balance has
 *   not been read yet.
 * @param walletId The wallet the import belongs to.
 * @param address The wallet address the balance was read against.
 * @returns The holding row for the imported token.
 */
function importedRow(
  token: ImportedToken,
  line: BalanceLine | null,
  walletId: string,
  address: string,
): HoldingRow {
  // The concrete badge chain is the pinned network when the import named one,
  // otherwise the chain family key. The muted subtitle prefers the network's own
  // label so a Base import reads "Base" rather than the generic family.
  const badge = token.network ?? token.chain;
  return {
    symbol: token.symbol,
    name: token.name || token.symbol,
    network: chainLabel(badge),
    chain: token.chain,
    badge,
    // An imported row is a token by construction: the import flow adds a
    // contract address, and a network's native coin has none.
    isNative: false,
    native: line ? line.amount : '0',
    // An imported token never carries a priced value, so its fiat is null and it
    // sorts and dusts as an unpriced row. This is what keeps a null fiat from
    // inflating the total.
    fiat: null,
    price: null,
    change24h: null,
    walletId,
    address,
    // Always unverified: an imported token is outside the curated allow-list.
    verified: false,
  };
}

/**
 * Match one imported token to its appended balance line within a wallet's
 * balances view. The gateway appends an imported contract's balance as an extra
 * token entry, so this finds the entry whose symbol matches the resolved import.
 * A symbol match is sufficient because the imports the gateway appends are exactly
 * the contracts the client asked it to append, so the appended set and the stored
 * set line up one to one; when no entry matches, the row falls back to a zero
 * balance until the next poll fills it in.
 *
 * @param token The stored imported token.
 * @param view The wallet's balances view, or undefined before it loads.
 * @returns The matching appended balance line, or null.
 */
function matchLine(token: ImportedToken, view: WalletBalances | undefined): BalanceLine | null {
  if (!view) return null;
  const symbol = token.symbol.toUpperCase();
  for (const line of view.tokens) {
    // Only an unverified appended line can be an import; a curated line that
    // happens to share the symbol is left to the curated aggregation.
    if (line.verified) continue;
    if (line.symbol.toUpperCase() === symbol) return line;
  }
  return null;
}

/**
 * Merge a wallet's imported tokens into its curated holding rows. Each imported
 * token becomes one row carrying its live balance from the balances view, stamped
 * unverified with a null fiat, and appended after the curated rows so the sorted
 * curated list is left intact and the unpriced imports settle at the end alongside
 * other unpriced rows. Duplicates in the stored list are collapsed first so a
 * token imported twice yields a single row. An import whose row already appears in
 * the curated rows, meaning the gateway also returned it as a curated balance, is
 * skipped so the same asset is not listed twice.
 *
 * The merged rows still pass through the caller's existing hidden, dust, and tier
 * filters unchanged, so an imported token composes with every rule a curated token
 * obeys rather than bypassing them.
 *
 * @param curatedRows The wallet's curated holding rows, already sorted.
 * @param tokens The wallet's imported tokens.
 * @param view The wallet's balances view, for the live imported balances, or
 *   undefined before it loads.
 * @param walletId The wallet the imports belong to.
 * @param address The wallet address the balances were read against.
 * @returns The curated rows followed by one row per distinct imported token.
 */
export function mergeImportedIntoRows(
  curatedRows: readonly HoldingRow[],
  tokens: readonly ImportedToken[],
  view: WalletBalances | undefined,
  walletId: string,
  address: string,
): HoldingRow[] {
  if (tokens.length === 0) return curatedRows.slice();

  // The identity keys already present as curated rows, so an import the gateway
  // also returned as a curated balance is not duplicated. A curated row has no
  // contract, so it is keyed by its badge chain and symbol, and an import is
  // suppressed only when a curated row on the same badge chain carries the same
  // symbol.
  const curatedBadgeSymbols = new Set(
    curatedRows.map((row) => `${row.badge}::${row.symbol.toUpperCase()}`),
  );

  const rows = curatedRows.slice();
  for (const token of dedupeImported(tokens)) {
    const badge = token.network ?? token.chain;
    const badgeSymbol = `${badge}::${token.symbol.toUpperCase()}`;
    if (curatedBadgeSymbols.has(badgeSymbol)) continue;
    rows.push(importedRow(token, matchLine(token, view), walletId, address));
  }
  return rows;
}
